/**
 * The Organization Overview's pure rules: one normalised tree, the client-side
 * search, the remembered layout and the route each node opens.
 *
 * The server returns ONE nested, already-scoped tree. Every layout is drawn
 * from the tree built here, so a layout can neither show a node another hides
 * nor compute a count of its own: counts are the server's, and nothing here
 * decides what a caller may see -- `inScope` is read back, never derived.
 *
 * No role code appears in this module. A holder's KIND (delivery, sales or
 * project head) is chosen from the delivery-role code the server attached to
 * the owner, and only to pick an icon and a column.
 */

import type {
  CurrentOwner,
  OrganizationOverview,
  OverviewClient,
  OverviewCounts,
  OverviewEngagement,
  OverviewGroup,
  OverviewUnit,
} from '@/api/client';

export type Level = 'group' | 'unit' | 'client' | 'engagement';

export const LEVEL_LABEL: Readonly<Record<Level, string>> = {
  group: 'Business Unit Group',
  unit: 'Business Unit',
  client: 'Client',
  engagement: 'Engagement',
};

export const CHILD_LABEL: Readonly<Record<Level, string>> = {
  group: 'Business Units',
  unit: 'Clients',
  client: 'Engagements',
  engagement: '',
};

export type HolderKind = 'delivery' | 'sales' | 'head';

/** Delivery-role code to the layout's three holder kinds. Icon and column only. */
const KIND_BY_ROLE: Readonly<Record<string, HolderKind>> = {
  DEM: 'delivery',
  DM: 'delivery',
  CEM: 'sales',
  CM: 'sales',
  PROJECT_HEAD: 'head',
};

export const EMPTY_COUNTS: OverviewCounts = {
  business_units: 0,
  clients: 0,
  engagements: 0,
  active_engagements: 0,
  high_risk: 0,
  medium_risk: 0,
  low_risk: 0,
};

export interface OrgNode {
  id: string;
  level: Level;
  code: string;
  name: string;
  isActive: boolean;
  statusName: string;
  /** `false` for a context ancestor: label only, no link, no owners. */
  inScope: boolean;
  owners: readonly CurrentOwner[];
  delivery: CurrentOwner | null;
  sales: CurrentOwner | null;
  head: CurrentOwner | null;
  counts: OverviewCounts;
  children: OrgNode[];
  /** Ancestors' ids from the top down, excluding this node. */
  ancestors: readonly string[];
  /** The Client this node belongs to, for the engagement route. */
  clientId: string | null;
  // Engagement-only.
  riskLevel: string | null;
  startDate: string | null;
  endDate: string | null;
  services: string | null;
}

export interface OrgTree {
  roots: OrgNode[];
  byId: ReadonlyMap<string, OrgNode>;
  totals: OverviewCounts;
}

interface Raw {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  in_scope: boolean;
  status: { name: string };
  owners: CurrentOwner[];
}

function holder(owners: readonly CurrentOwner[], kind: HolderKind): CurrentOwner | null {
  return owners.find((owner) => KIND_BY_ROLE[owner.delivery_role_code] === kind) ?? null;
}

function node(
  raw: Raw,
  level: Level,
  counts: OverviewCounts,
  ancestors: readonly string[],
  clientId: string | null,
): OrgNode {
  return {
    id: raw.id,
    level,
    code: raw.code,
    name: raw.name,
    isActive: raw.is_active,
    statusName: raw.status.name,
    inScope: raw.in_scope,
    owners: raw.owners,
    delivery: holder(raw.owners, 'delivery'),
    sales: holder(raw.owners, 'sales'),
    head: holder(raw.owners, 'head'),
    counts,
    children: [],
    ancestors,
    clientId,
    riskLevel: null,
    startDate: null,
    endDate: null,
    services: null,
  };
}

function engagementNode(raw: OverviewEngagement, ancestors: readonly string[], clientId: string): OrgNode {
  const built = node(raw, 'engagement', { ...EMPTY_COUNTS, engagements: 1 }, ancestors, clientId);
  built.riskLevel = raw.risk_level ?? null;
  built.startDate = raw.start_date ?? null;
  built.endDate = raw.end_date ?? null;
  built.services = raw.services ?? null;
  return built;
}

function clientNode(raw: OverviewClient, ancestors: readonly string[]): OrgNode {
  const built = node(raw, 'client', raw.counts, ancestors, raw.id);
  const below = [...ancestors, raw.id];
  built.children = raw.engagements.map((child) => engagementNode(child, below, raw.id));
  return built;
}

function unitNode(raw: OverviewUnit, ancestors: readonly string[]): OrgNode {
  const built = node(raw, 'unit', raw.counts, ancestors, null);
  const below = [...ancestors, raw.id];
  built.children = raw.clients.map((child) => clientNode(child, below));
  return built;
}

function groupNode(raw: OverviewGroup): OrgNode {
  const built = node(raw, 'group', raw.counts, [], null);
  built.children = raw.business_units.map((child) => unitNode(child, [raw.id]));
  return built;
}

export function buildTree(overview: OrganizationOverview): OrgTree {
  const roots = overview.business_unit_groups.map(groupNode);
  const byId = new Map<string, OrgNode>();
  const index = (current: OrgNode) => {
    byId.set(current.id, current);
    current.children.forEach(index);
  };
  roots.forEach(index);
  return { roots, byId, totals: overview.totals };
}

// --- Search -----------------------------------------------------------------

export interface TreeFilter {
  search: string;
  activeOnly: boolean;
}

export function isFiltering(filter: TreeFilter): boolean {
  return filter.search.trim() !== '' || filter.activeOnly;
}

function matches(current: OrgNode, needle: string): boolean {
  if (current.name.toLowerCase().includes(needle) || current.code.toLowerCase().includes(needle)) {
    return true;
  }
  return current.owners.some((owner) => owner.display_name.toLowerCase().includes(needle));
}

/**
 * The tree narrowed by the caller's own search and the active-only switch.
 *
 * A node stays when it matches or when something beneath it does; a node that
 * matches itself keeps its whole subtree, so searching a client still shows
 * its engagements. Inactive nodes drop with everything beneath them. Returns
 * copies: the indexed tree is never mutated.
 */
export function filterTree(roots: readonly OrgNode[], filter: TreeFilter): OrgNode[] {
  const needle = filter.search.trim().toLowerCase();
  if (!isFiltering(filter)) return [...roots];

  const visit = (current: OrgNode, inherited: boolean): OrgNode | null => {
    if (filter.activeOnly && !current.isActive) return null;
    const self = needle === '' || inherited || matches(current, needle);
    const children = current.children
      .map((child) => visit(child, self && needle !== ''))
      .filter((child): child is OrgNode => child !== null);
    if (!self && children.length === 0) return null;
    return { ...current, children };
  };
  return roots.map((root) => visit(root, false)).filter((root): root is OrgNode => root !== null);
}

/** Every node id in the (filtered) tree that has children, for expand-all. */
export function expandableIds(roots: readonly OrgNode[]): string[] {
  const found: string[] = [];
  const visit = (current: OrgNode) => {
    if (current.children.length > 0) found.push(current.id);
    current.children.forEach(visit);
  };
  roots.forEach(visit);
  return found;
}

// --- Routes -----------------------------------------------------------------

/**
 * Where a node opens, or `null` for a context ancestor the caller may not open.
 * An engagement has no screen of its own (ADR-17): it opens its client's
 * Engagements tab.
 */
export function detailPath(current: OrgNode, byId: ReadonlyMap<string, OrgNode>): string | null {
  if (!current.inScope) return null;
  switch (current.level) {
    case 'group':
      return `/business-unit-groups/${current.id}`;
    case 'unit':
      return `/business-units/${current.id}`;
    case 'client':
      return `/clients/${current.id}`;
    case 'engagement': {
      const parent = current.clientId ? byId.get(current.clientId) : undefined;
      return parent?.inScope ? `/clients/${parent.id}?tab=engagements` : null;
    }
  }
}

// --- Presentation helpers ---------------------------------------------------

export function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .filter((letter) => /\p{Lu}/u.test(letter))
    .slice(0, 2)
    .join('');
  return letters || name.slice(0, 2).toUpperCase();
}

export interface RiskMix {
  high: number;
  medium: number;
  low: number;
  total: number;
}

export function riskMix(counts: OverviewCounts): RiskMix {
  const total = counts.high_risk + counts.medium_risk + counts.low_risk;
  return { high: counts.high_risk, medium: counts.medium_risk, low: counts.low_risk, total };
}

export function riskClass(riskLevel: string | null): 'high' | 'medium' | 'low' | null {
  const key = riskLevel?.trim().toLowerCase();
  return key === 'high' || key === 'medium' || key === 'low' ? key : null;
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** The one-line subtitle a node carries in a list: what lies beneath it. */
export function subtitleOf(current: OrgNode): string {
  const { counts } = current;
  switch (current.level) {
    case 'group':
      return `${pluralise(counts.business_units, 'unit')} · ${pluralise(counts.clients, 'client')}`;
    case 'unit':
      return `${pluralise(counts.clients, 'client')} · ${pluralise(counts.engagements, 'engagement')}`;
    case 'client':
      return pluralise(counts.engagements, 'engagement');
    case 'engagement':
      return current.services ?? current.code;
  }
}

// --- The remembered layout --------------------------------------------------

export const VIEWS = [
  { id: 'tree', label: 'Tree table' },
  { id: 'columns', label: 'Columns' },
  { id: 'cards', label: 'Card drill-down' },
  { id: 'blocks', label: 'Block chart' },
] as const;

export type ViewId = (typeof VIEWS)[number]['id'];

export const DEFAULT_VIEW: ViewId = 'tree';

export function isViewId(value: unknown): value is ViewId {
  return VIEWS.some((view) => view.id === value);
}

/** One key per signed-in user, so two people on a shared browser keep their own. */
export function viewStorageKey(userId: string | null | undefined): string {
  return `atlas.organization-overview.view.${userId ?? 'anonymous'}`;
}

/** Storage may be absent, full or blocked: every access is guarded, and the default holds. */
export function loadView(userId: string | null | undefined): ViewId {
  try {
    const stored = window.localStorage.getItem(viewStorageKey(userId));
    return isViewId(stored) ? stored : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

export function saveView(userId: string | null | undefined, view: ViewId): void {
  try {
    window.localStorage.setItem(viewStorageKey(userId), view);
  } catch {
    /* A remembered layout is a convenience, never a requirement. */
  }
}
